import loadable from '@loadable/component';
import { RouterLink } from '@redux-first-router-link';

import { Grid, Box } from '@simplywallst/ui-core';
import { Heading, Button } from '@simplywallst/ui-core/next';

import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';

import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';

import { ROUTE_PORTFOLIO_NEW } from '@/constants/routes';

import { useAggregatePortfolio } from '@/hooks/portfolio/useAggregatePortfolio';

import { AggregatePortfolioPerformance } from '../AggregatePortfolioPerformance';
import { AggregatePortfolioSnowflake } from '../AggregatePortfolioSnowflake';
import { LoadingState } from './LoadingState';
import { PortfolioDropdown } from '../../../../components/PortfolioDropdown';

const PortfolioLimitsBanner = loadable(
  () => import('../../../../components/PortfolioLimitsBanner'),
  {
    resolveComponent: (c) => c.PortfolioLimitsBanner,
  }
);

export const AggregatePortfolioOverview = () => {
  const { data: aggregatePortfolio, status } = useAggregatePortfolio();

  const isLoading = status === 'pending';
  const isError = status === 'error';

  if (isLoading) return <LoadingState />;
  if (isError || !aggregatePortfolio) return null;

  const shouldShowPortfolioLimitsBanner =
    aggregatePortfolio?.analysis.isAnalysisRestricted;

  return (
    <Grid
      gap="x2"
      templateRows={`repeat(${shouldShowPortfolioLimitsBanner ? 3 : 2}, auto)`}
    >
      <Grid
        as="header"
        data-cy-id="my-portfolios-header"
        justifyContent="space-between"
        alignItems="center"
        paddingXLg="x0"
        paddingY="x2"
        paddingX="x2"
        templateColumns="max-content max-content"
      >
        <div className="-translate-x-x1">
          <PortfolioDropdown portfolioId={null}>
            <Button
              variant="subtle"
              className="mr-x1 grid grid-cols-[auto_24px] items-center px-x0_75"
            >
              <Heading
                as="h1"
                size="mega"
                className="w-full truncate text-nowrap text-solid"
              >
                All Portfolios
              </Heading>
              <ChevronDownSmallSolid className="fill-soft" />
            </Button>
          </PortfolioDropdown>
        </div>
        <RouterLink to={{ type: ROUTE_PORTFOLIO_NEW }}>
          <Button variant="primary" size="medium" onClick={() => {}}>
            <PlusSolid className="fill-brand-01" /> New portfolio
          </Button>
        </RouterLink>
      </Grid>

      {shouldShowPortfolioLimitsBanner && (
        <Box marginBottom="x2" minHeight="56px">
          <PortfolioLimitsBanner
            data-cy-id="portfolio-limits-banner"
            content="Some of your portfolios are limited by your plan, affecting the total metrics."
          />
        </Box>
      )}
      <Grid
        templateRows="auto auto"
        templateRowsMd="auto"
        templateColumnsMd="auto 32%"
        gap="16px"
        data-cy-id="aggregate-portfolio-overview"
      >
        <Grid
          gapLg="4px"
          backgroundColor="noir"
          borderRadiusLg="x1"
          data-cy-id="aggregate-portfolio-performance"
        >
          <AggregatePortfolioPerformance
            aggregatePortfolio={aggregatePortfolio}
          />
        </Grid>
        <Grid
          backgroundColor="noir"
          padding="x2"
          borderRadius="x1"
          gap="16px"
          data-cy-id="aggregate-portfolio-snowflake"
        >
          <AggregatePortfolioSnowflake
            aggregatePortfolio={aggregatePortfolio}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
